import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { CheckOutlined, PlusOutlined, EditOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import AddressSkeleton from "../components/AddressSkeleton";
import OrderSummarySkeleton from "../components/OrderSummarySkeleton";
import { useNavigate } from "react-router";

const Container = styled.div`
  min-height: 100vh;
  background: #fff;
`;

const CheckoutWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const CheckoutHeading = styled.h1`
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 40px;
  color: #000;

  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 30px;
  }
`;

const CheckoutContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 40px;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Section = styled.div`
  background: #fff;
  border: 1px solid #eee;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;

  ${(props) =>
    props.active
      ? `
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  `
      : `
    opacity: 0.6;
    pointer-events: ${props.completed ? "auto" : "none"};
  `}
`;

const SectionHeader = styled.button`
  width: 100%;
  padding: 20px;
  background: #f9f9f9;
  border: none;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.3s ease;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};

  &:hover {
    background: ${(props) => (props.disabled ? "#f9f9f9" : "#f0f0f0")};
  }

  h2 {
    font-size: 18px;
    font-weight: 700;
    color: #000;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 12px;
  }
`;

const SectionNumber = styled.span`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: #666;
  font-size: 14px;

  ${(props) =>
    props.completed &&
    `
    background: #000;
    color: #fff;
  `}
`;

const CompletedIcon = styled(CheckOutlined)`
  font-size: 16px;
  margin-right: 8px;
`;

const SectionContent = styled.div`
  max-height: ${(props) => (props.open ? "1000px" : "0")};
  overflow: hidden;
  transition: max-height 0.4s ease;
  padding: ${(props) => (props.open ? "20px" : "0 20px")};
  border-top: ${(props) => (props.open ? "1px solid #eee" : "none")};
`;

// Address Section Styles
const AddressGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
`;

const AddressCard = styled.div`
  border: 2px solid ${(props) => (props.selected ? "#000" : "#eee")};
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.3s;
  background: ${(props) => (props.selected ? "#f0f0f0" : "#fff")};

  &:hover {
    border-color: #000;
  }

  h4 {
    margin: 0 0 8px 0;
    font-size: 14px;
    font-weight: 700;
    color: #000;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  p {
    margin: 4px 0;
    font-size: 13px;
    color: #666;
    line-height: 1.5;
  }
`;

const AddNewAddressBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  border: 2px dashed #ddd;
  background: #fff;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: 600;
  color: #000;
  font-size: 14px;

  &:hover {
    border-color: #000;
    background: #f9f9f9;
  }
`;

const AddressForm = styled.form`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;
  padding: 20px;
  background: #f9f9f9;
  border-radius: 8px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  ${(props) => props.fullWidth && `grid-column: 1 / -1;`}

  label {
    font-size: 13px;
    font-weight: 600;
    color: #000;
  }

  input,
  textarea {
    padding: 10px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    font-family: inherit;
    transition: border-color 0.3s;

    &:focus {
      outline: none;
      border-color: #000;
    }
  }

  textarea {
    resize: vertical;
    min-height: 80px;
  }

  .hint {
    font-size: 11px;
    color: #999;
    margin-top: 4px;
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;

  button {
    padding: 10px 20px;
    border-radius: 4px;
    border: none;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s;
  }
`;

const CancelBtn = styled.button`
  background: #f0f0f0;
  color: #000;

  &:hover:not(:disabled) {
    background: #e0e0e0;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SaveBtn = styled.button`
  background: #000;
  color: #fff;

  &:hover:not(:disabled) {
    background: #333;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const ContinueBtn = styled.button`
  width: 100%;
  padding: 14px;
  background: #000;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s;
  margin-top: 20px;

  &:hover {
    background: #333;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const PaymentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PaymentSection = styled.div`
  h4 {
    font-size: 14px;
    font-weight: 700;
    color: #000;
    margin: 0 0 12px 0;
    text-transform: uppercase;
  }

  p {
    margin: 8px 0;
    font-size: 13px;
    color: #666;
    line-height: 1.6;
  }
`;

const AddressSummary = styled.div`
  background: #f9f9f9;
  padding: 16px;
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.6;
  color: #666;
`;

const OrderSummary = styled.div`
  background: #f9f9f9;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 30px;
  height: fit-content;
  position: sticky;
  top: 20px;

  @media (max-width: 968px) {
    position: static;
    padding: 20px;
    margin-top: 20px;
  }
`;

const EmptyCartContainer = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #eee;
`;

const EmptyCartMessage = styled.div`
  margin-bottom: 30px;

  h2 {
    font-size: 24px;
    font-weight: 700;
    color: #000;
    margin: 0 0 10px 0;
  }

  p {
    font-size: 14px;
    color: #666;
    margin: 0;
    line-height: 1.6;
  }
`;

const ReturnCartButton = styled.button`
  padding: 14px 30px;
  background: #000;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background: #333;
  }
`;

const SummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #eee;
  font-size: 13px;

  &:last-child {
    border-bottom: none;
  }

  &.total {
    border-top: 2px solid #000;
    border-bottom: 2px solid #000;
    padding: 15px 0;
    font-size: 15px;
    font-weight: 700;
    margin: 15px 0;
  }

  .label {
    color: #666;
  }

  .value {
    color: #000;
    font-weight: 600;
  }
`;

const ShippingNote = styled.div`
  font-size: 11px;
  color: #999;
  font-weight: 500;
  padding: 0 0 12px 0;
  border-bottom: 1px solid #eee;
`;

const OrderItem = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
  padding: 12px;
  /* background: #fff; */
  border-radius: 6px;
  border: 1px solid #eee;

  img {
    width: 60px;
    height: 60px;
    object-fit: contain;
    border-radius: 4px;
  }

  .details {
    flex: 1;

    .name {
      font-size: 13px;
      font-weight: 600;
      color: #000;
      margin: 0 0 4px 0;
    }

    .qty {
      font-size: 12px;
      color: #666;
      margin: 0;
    }
  }

  .price {
    font-size: 13px;
    font-weight: 600;
    color: #000;
  }
`;

const ProceedPaymentBtn = styled.button`
  width: 100%;
  padding: 16px;
  background: #000;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  margin-top: 20px;
  transition: all 0.3s;

  &:hover {
    background: #333;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const EmptyAddressContainer = styled.div`
  text-align: center;
  padding: 40px 20px;
`;

const EmptyAddressMessage = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 20px;
`;

const EditModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const EditModalDialog = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 30px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;

  h2 {
    font-size: 20px;
    font-weight: 700;
    margin: 0 0 20px 0;
    color: #000;
  }
`;

const AddressCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const EditButtonIcon = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #999;
  font-size: 16px;
  padding: 4px 8px;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #000;
  }
`;

const CouponSection = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  align-items: stretch;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const CouponInput = styled.input`
  flex: 1;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;
  transition: all 0.3s;

  &:focus {
    outline: none;
    border-color: #000;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
  }

  &:disabled {
    background: #f9f9f9;
    color: #999;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const CouponButton = styled.button`
  padding: 10px 20px;
  background: #000;
  color: #fff;
  border: 1px solid #000;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #333;
    border-color: #333;
  }

  &:disabled {
    background: #ccc;
    border-color: #ccc;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 10px 16px;
  }
`;

const CouponAppliedBox = styled.div`
  background: #f1f8f4;
  border: 1px solid #81c784;
  border-left: 4px solid #4caf50;
  border-radius: 4px;
  padding: 14px 16px;
  margin-bottom: 16px;
`;

const CouponAppliedHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  gap: 12px;
`;

const CouponAppliedTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #2e7d32;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const CouponRemoveBtn = styled.button`
  background: none;
  border: none;
  color: #d32f2f;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 8px;
  transition: all 0.3s;
  border-radius: 3px;

  &:hover {
    color: #b71c1c;
    background: rgba(211, 47, 47, 0.1);
  }
`;

const CouponInfo = styled.div`
  font-size: 13px;
  color: #333;
  line-height: 1.6;

  strong {
    color: #000;
    font-weight: 600;
  }

  span {
    color: #2e7d32;
    font-weight: 600;
  }

  div {
    margin-top: 6px;

    &:first-child {
      margin-top: 0;
    }
  }
`;

const CouponError = styled.div`
  font-size: 12px;
  color: #d32f2f;
  background: #ffebee;
  border: 1px solid #ef5350;
  border-radius: 4px;
  padding: 10px 12px;
  margin-top: 10px;
  line-height: 1.4;
`;

const DiscountRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #eee;
  font-size: 13px;

  .label {
    color: #666;
    font-weight: 500;
  }

  .value {
    color: #28a745;
    font-weight: 600;
  }
`;

// Indian States List
const INDIAN_STATES = [
  "Andaman and Nicobar Islands",
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chandigarh",
  "Chhattisgarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Ladakh",
  "Lakshadweep",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Puducherry",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
];

// Validation Functions
const validatePhone = (phone) => {
  const phoneRegex = /^[0-9]{10}$/;
  return phoneRegex.test(phone);
};

const validatePincode = (pincode) => {
  const pincodeRegex = /^[0-9]{6}$/;
  return pincodeRegex.test(pincode);
};

const validateAddressForm = (data) => {
  const errors = {};

  if (!data.name || data.name.trim() === "") {
    errors.name = "Name is required";
  }

  if (!data.phone || data.phone.trim() === "") {
    errors.phone = "Phone number is required";
  } else if (!validatePhone(data.phone)) {
    errors.phone = "Phone number must be exactly 10 digits";
  }

  if (!data.address || data.address.trim() === "") {
    errors.address = "Address is required";
  }

  if (!data.city || data.city.trim() === "") {
    errors.city = "City is required";
  }

  if (!data.state || data.state.trim() === "") {
    errors.state = "State is required";
  }

  if (!data.pincode || data.pincode.trim() === "") {
    errors.pincode = "Pincode is required";
  } else if (!validatePincode(data.pincode)) {
    errors.pincode = "Pincode must be exactly 6 digits";
  }

  return Object.keys(errors).length > 0 ? errors : null;
};

const Checkout = () => {
  const navigate = useNavigate();
  const token = useSelector((state) => state.token);

  const [activeSection, setActiveSection] = useState("address");
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [addressCompleted, setAddressCompleted] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [loadingAddresses, setLoadingAddresses] = useState(true);
  const [addressError, setAddressError] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    country: "India",
    is_default: false,
  });

  const [savingAddress, setSavingAddress] = useState(false);
  const [addressSaveError, setAddressSaveError] = useState(null);
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [editFormData, setEditFormData] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState(null);

  const [cartItems, setCartItems] = useState([]);
  const [loadingCart, setLoadingCart] = useState(true);
  const [cartError, setCartError] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [discount, setDiscount] = useState(0);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState(null);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [processingCheckout, setProcessingCheckout] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const [razorpayKey, setRazorpayKey] = useState(null);
  const [loadingPaymentConfig, setLoadingPaymentConfig] = useState(true);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Fetch cart from API
  useEffect(() => {
    const fetchCart = async () => {
      try {
        setLoadingCart(true);
        setCartError(null);

        const response = await fetch(`${API_BASE_URL}/cart`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await response.json();

        if (data.status && data.data?.items) {
          // Map API response to cartItems format
          const mappedItems = data.data.items.map((item) => ({
            id: item.id,
            variantId: item.product_variant.id,
            name: item.product_variant.product.name,
            price: parseFloat(item.product_variant.price),
            quantity: item.quantity,
            image: item.product_variant.product.images?.[0]?.url,
            size: item.product_variant.size?.name || "N/A",
            color: item.product_variant.color?.name || "N/A",
          }));
          setCartItems(mappedItems);
        } else {
          setCartItems([]);
        }
      } catch (err) {
        console.error("Error fetching cart:", err);
        setCartError("Failed to load cart. Please try again.");
        setCartItems([]);
      } finally {
        setLoadingCart(false);
      }
    };

    if (token) {
      fetchCart();
    } else {
      setLoadingCart(false);
    }
  }, [token, API_BASE_URL]);

  // Fetch payment configuration
  useEffect(() => {
    const fetchPaymentConfig = async () => {
      try {
        setLoadingPaymentConfig(true);

        const response = await fetch(`${API_BASE_URL}/payment/config`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        const data = await response.json();

        if (data.status && data.data?.razorpay?.key_id) {
          setRazorpayKey(data.data.razorpay.key_id);
        } else {
          console.error("Failed to fetch Razorpay key");
        }
      } catch (err) {
        console.error("Error fetching payment config:", err);
      } finally {
        setLoadingPaymentConfig(false);
      }
    };

    if (API_BASE_URL) {
      fetchPaymentConfig();
    }
  }, [API_BASE_URL]);

  // Fetch addresses from API
  useEffect(() => {
    const fetchAddresses = async () => {
      try {
        setLoadingAddresses(true);
        setAddressError(null);

        const response = await fetch(`${API_BASE_URL}/addresses`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await response.json();

        if (data.status && Array.isArray(data.data)) {
          // Map API response to address format
          const mappedAddresses = data.data.map((item) => ({
            id: item.id,
            name: item.name,
            street: item.address,
            city: item.city,
            state: item.state,
            pincode: item.pincode,
            phone: item.phone,
            country: item.country,
            is_default: item.is_default,
          }));
          setSavedAddresses(mappedAddresses);
        } else {
          setSavedAddresses([]);
        }
      } catch (err) {
        console.error("Error fetching addresses:", err);
        setAddressError("Failed to load addresses. Please try again.");
        setSavedAddresses([]);
      } finally {
        setLoadingAddresses(false);
      }
    };

    if (token) {
      fetchAddresses();
    } else {
      setLoadingAddresses(false);
    }
  }, [token, API_BASE_URL]);

  const subtotal = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0,
  );
  const SHIPPING_COST = subtotal > 499 ? 0 : 100;
  const tax = 0;
  const total = subtotal + SHIPPING_COST - discount;

  const handleAddressSelect = (address) => {
    setSelectedAddress(address);
    setAddressCompleted(true);
    setActiveSection("payment");
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveAddress = async (e) => {
    e.preventDefault();

    // Validate form
    const validationErrors = validateAddressForm(formData);
    if (validationErrors) {
      const errorMessage = Object.values(validationErrors).join("\n");
      setAddressSaveError(errorMessage);
      return;
    }

    setSavingAddress(true);
    setAddressSaveError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/addresses`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode,
          country: formData.country,
          is_default: formData.is_default,
        }),
      });

      const data = await response.json();

      if (data.status || response.ok) {
        // Add new address to the list
        const newAddress = {
          id: data.data?.id || Date.now(),
          name: formData.name,
          street: formData.address,
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode,
          phone: formData.phone,
          country: formData.country,
          is_default: formData.is_default,
        };

        setSavedAddresses((prev) => [newAddress, ...prev]);
        setSelectedAddress(newAddress);
        setAddressCompleted(true);
        setActiveSection("payment");
        setShowAddressForm(false);

        // Reset form
        setFormData({
          name: "",
          phone: "",
          address: "",
          city: "",
          state: "",
          pincode: "",
          country: "India",
          is_default: false,
        });
      } else {
        setAddressSaveError(
          data.message || "Failed to save address. Please try again.",
        );
      }
    } catch (err) {
      console.error("Error saving address:", err);
      setAddressSaveError("Failed to save address. Please try again.");
    } finally {
      setSavingAddress(false);
    }
  };

  const handleCancelForm = () => {
    setShowAddressForm(false);
    setAddressSaveError(null);
    setFormData({
      name: "",
      phone: "",
      address: "",
      city: "",
      state: "",
      pincode: "",
      country: "India",
      is_default: false,
    });
  };

  const initiateEditAddress = (address) => {
    setEditingAddressId(address.id);
    setEditFormData({
      name: address.name,
      phone: address.phone,
      address: address.street,
      city: address.city,
      state: address.state,
      pincode: address.pincode,
      country: address.country,
      is_default: address.is_default,
    });
    setEditError(null);
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    if (!editFormData || !editingAddressId) return;

    // Validate form
    const validationErrors = validateAddressForm(editFormData);
    if (validationErrors) {
      const errorMessage = Object.values(validationErrors).join("\n");
      setEditError(errorMessage);
      return;
    }

    setSavingEdit(true);
    setEditError(null);

    try {
      const response = await fetch(
        `${API_BASE_URL}/addresses/${editingAddressId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            name: editFormData.name,
            phone: editFormData.phone,
            address: editFormData.address,
            city: editFormData.city,
            state: editFormData.state,
            pincode: editFormData.pincode,
            country: editFormData.country,
            is_default: editFormData.is_default,
          }),
        },
      );

      const data = await response.json();

      if (data.status || response.ok) {
        // Update address in the list
        setSavedAddresses((prev) =>
          prev.map((addr) =>
            addr.id === editingAddressId
              ? {
                  ...addr,
                  name: editFormData.name,
                  street: editFormData.address,
                  city: editFormData.city,
                  state: editFormData.state,
                  pincode: editFormData.pincode,
                  phone: editFormData.phone,
                  country: editFormData.country,
                  is_default: editFormData.is_default,
                }
              : addr,
          ),
        );

        // If this was the selected address, update it
        if (selectedAddress?.id === editingAddressId) {
          setSelectedAddress({
            ...selectedAddress,
            name: editFormData.name,
            street: editFormData.address,
            city: editFormData.city,
            state: editFormData.state,
            pincode: editFormData.pincode,
            phone: editFormData.phone,
            country: editFormData.country,
            is_default: editFormData.is_default,
          });
        }

        setEditingAddressId(null);
        setEditFormData(null);
      } else {
        setEditError(
          data.message || "Failed to update address. Please try again.",
        );
      }
    } catch (err) {
      console.error("Error updating address:", err);
      setEditError("Failed to update address. Please try again.");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingAddressId(null);
    setEditFormData(null);
    setEditError(null);
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError("Please enter a coupon code");
      return;
    }

    try {
      setCouponLoading(true);
      setCouponError(null);

      const response = await fetch(`${API_BASE_URL}/coupons/apply`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ code: couponCode.toUpperCase() }),
      });

      const data = await response.json();

      if (data.status && data.data) {
        // Coupon applied successfully
        setAppliedCoupon(data.data);
        setDiscount(data.data.discount_amount || 0);
        setCouponCode("");
        setCouponError(null);
      } else {
        setCouponError(data.message || "Invalid coupon code");
        setAppliedCoupon(null);
        setDiscount(0);
      }
    } catch (err) {
      console.error("Error applying coupon:", err);
      setCouponError("Failed to apply coupon. Please try again.");
      setAppliedCoupon(null);
      setDiscount(0);
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setDiscount(0);
    setCouponCode("");
    setCouponError(null);
  };

  const handleCheckout = async () => {
    if (!selectedAddress) {
      setCheckoutError("Please select a delivery address");
      return;
    }

    if (!razorpayKey) {
      setCheckoutError(
        "Payment gateway is not configured. Please try again later.",
      );
      return;
    }

    setProcessingCheckout(true);
    setCheckoutError(null);

    try {
      const checkoutData = {
        address_id: selectedAddress.id,
        payment_method: "razorpay",
      };

      const response = await fetch(`${API_BASE_URL}/orders/checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(checkoutData),
      });

      const data = await response.json();

      if (data.status && data.data) {
        // Order created successfully, open Razorpay
        openRazorpayPayment(data.data);
      } else {
        setCheckoutError(
          data.message || "Failed to create order. Please try again.",
        );
      }
    } catch (err) {
      console.error("Error during checkout:", err);
      setCheckoutError("Failed to process checkout. Please try again.");
    } finally {
      setProcessingCheckout(false);
    }
  };

  const openRazorpayPayment = (orderData) => {
    if (!razorpayKey) {
      setCheckoutError(
        "Payment gateway not configured. Please try again later.",
      );
      return;
    }

    const options = {
      key: razorpayKey,
      amount: Math.round(parseFloat(orderData.total) * 100), // Convert to paise
      currency: "INR",
      name: "GenzWearables",
      description: `Order ${orderData.order_number}`,
      order_id: orderData.transaction_id,
      handler: async function (response) {
        // Update payment status in backend
        try {
          const paymentConfirmData = {
            payment_status: "paid",
            transaction_id: response.razorpay_payment_id,
            payment_method: "razorpay",
          };

          const confirmResponse = await fetch(
            `${API_BASE_URL}/orders/${orderData.id}/payment`,
            {
              method: "PATCH",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify(paymentConfirmData),
            },
          );

          const confirmData = await confirmResponse.json();

          if (confirmData.status || confirmResponse.ok) {
            // Payment confirmed successfully, navigate to order confirmation
            navigate(`/order-confirmation/${orderData.id}`, {
              state: {
                orderData: orderData,
                paymentData: response,
                paymentConfirmed: true,
              },
            });
          } else {
            // Payment confirmation failed but payment was made
            setCheckoutError(
              confirmData.message ||
                "Payment made but confirmation failed. Please check your orders.",
            );
          }
        } catch (err) {
          console.error("Error confirming payment:", err);
          // Even if confirmation fails, redirect to order confirmation
          // since payment was already made
          navigate(`/order-confirmation/${orderData.id}`, {
            state: {
              orderData: orderData,
              paymentData: response,
              paymentConfirmed: true,
            },
          });
        }
      },
      prefill: {
        name: selectedAddress?.name || "",
        email: "", // Add if available
        contact: selectedAddress?.phone || "",
      },
      theme: {
        color: "#000000",
      },
      modal: {
        ondismiss: function () {
          setCheckoutError("Payment was cancelled. Please try again.");
          setProcessingCheckout(false);
        },
      },
    };

    const razorpayScript = document.createElement("script");
    razorpayScript.src = "https://checkout.razorpay.com/v1/checkout.js";
    razorpayScript.async = true;
    razorpayScript.onload = () => {
      const rzp = new window.Razorpay(options);
      rzp.open();
    };
    razorpayScript.onerror = () => {
      setCheckoutError("Failed to load payment gateway. Please try again.");
    };
    document.body.appendChild(razorpayScript);
  };

  return (
    <Container>
      {/* <Navbar /> */}

      <CheckoutWrapper>
        <CheckoutHeading>Checkout</CheckoutHeading>

        {!loadingCart && cartItems.length === 0 ? (
          <EmptyCartContainer>
            <EmptyCartMessage>
              <h2>Your Cart is Empty</h2>
              <p>
                You don't have any items in your cart. Please add some products
                before proceeding to checkout.
              </p>
            </EmptyCartMessage>
            <ReturnCartButton onClick={() => navigate("/cart")}>
              ← Return to Cart
            </ReturnCartButton>
          </EmptyCartContainer>
        ) : (
          <CheckoutContent>
            <SectionContainer>
              {/* Address Section */}
              <Section active={activeSection === "address"} completed={true}>
                <SectionHeader
                  onClick={() =>
                    activeSection !== "address" && setActiveSection("address")
                  }
                  disabled={false}
                >
                  <h2>
                    <SectionNumber completed={addressCompleted}>
                      {addressCompleted ? <CompletedIcon /> : "1"}
                    </SectionNumber>
                    Delivery Address
                  </h2>
                  <span>{activeSection === "address" ? "−" : "+"}</span>
                </SectionHeader>

                <SectionContent open={activeSection === "address"}>
                  {loadingAddresses ? (
                    <div style={{ paddingTop: "10px" }}>
                      <AddressSkeleton count={2} />
                    </div>
                  ) : addressError ? (
                    <EmptyAddressContainer>
                      <p style={{ color: "#d32f2f", marginBottom: "20px" }}>
                        {addressError}
                      </p>
                      <AddNewAddressBtn
                        type="button"
                        onClick={() => setShowAddressForm(true)}
                      >
                        <PlusOutlined />
                        Add Address
                      </AddNewAddressBtn>
                    </EmptyAddressContainer>
                  ) : !showAddressForm ? (
                    <>
                      {savedAddresses.length > 0 ? (
                        <>
                          <h3
                            style={{
                              fontSize: "14px",
                              fontWeight: "600",
                              marginBottom: "12px",
                              color: "#000",
                            }}
                          >
                            Saved Addresses
                          </h3>
                          <AddressGrid>
                            {savedAddresses.map((address) => (
                              <AddressCard
                                key={address.id}
                                selected={selectedAddress?.id === address.id}
                                onClick={() => handleAddressSelect(address)}
                              >
                                <AddressCardHeader>
                                  <div>
                                    {address.name}
                                    {selectedAddress?.id === address.id && (
                                      <CheckOutlined
                                        style={{
                                          color: "#000",
                                          fontSize: "16px",
                                          marginLeft: "8px",
                                        }}
                                      />
                                    )}
                                  </div>
                                  <EditButtonIcon
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      initiateEditAddress(address);
                                    }}
                                  >
                                    <EditOutlined />
                                  </EditButtonIcon>
                                </AddressCardHeader>
                                <p>{address.street}</p>
                                <p>
                                  {address.city}, {address.state}{" "}
                                  {address.pincode}
                                </p>
                                <p>{address.phone}</p>
                              </AddressCard>
                            ))}
                          </AddressGrid>
                        </>
                      ) : (
                        <EmptyAddressContainer>
                          <EmptyAddressMessage>
                            No addresses found. Please add a delivery address to
                            continue.
                          </EmptyAddressMessage>
                        </EmptyAddressContainer>
                      )}

                      <AddNewAddressBtn
                        type="button"
                        onClick={() => setShowAddressForm(true)}
                      >
                        <PlusOutlined />
                        Add New Address
                      </AddNewAddressBtn>
                    </>
                  ) : (
                    <AddressForm onSubmit={handleSaveAddress}>
                      {addressSaveError && (
                        <div
                          style={{
                            gridColumn: "1 / -1",
                            background: "#f8d7da",
                            color: "#721c24",
                            padding: "12px",
                            borderRadius: "4px",
                            fontSize: "13px",
                            marginBottom: "10px",
                          }}
                        >
                          {addressSaveError}
                        </div>
                      )}

                      <FormGroup>
                        <label>Name</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleFormChange}
                          placeholder="Enter name"
                          required
                        />
                      </FormGroup>

                      <FormGroup>
                        <label>Phone Number</label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleFormChange}
                          placeholder="Enter 10-digit phone number"
                          maxLength="10"
                          inputMode="numeric"
                          required
                        />
                        {formData.phone && !validatePhone(formData.phone) && (
                          <span
                            style={{
                              fontSize: "12px",
                              color: "#d32f2f",
                              marginTop: "4px",
                            }}
                          >
                            Phone number must be exactly 10 digits
                          </span>
                        )}
                      </FormGroup>

                      <FormGroup fullWidth>
                        <label>Address</label>
                        <textarea
                          name="address"
                          value={formData.address}
                          onChange={handleFormChange}
                          placeholder="Enter street address, house no., building name"
                          required
                        />
                      </FormGroup>

                      <FormGroup>
                        <label>City</label>
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleFormChange}
                          placeholder="Enter city"
                          required
                        />
                      </FormGroup>

                      <FormGroup>
                        <label>State</label>
                        <select
                          name="state"
                          value={formData.state}
                          onChange={handleFormChange}
                          required
                          style={{
                            padding: "10px 12px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            fontSize: "14px",
                            fontFamily: "inherit",
                            transition: "border-color 0.3s",
                            cursor: "pointer",
                            backgroundColor: "#fff",
                          }}
                        >
                          <option value="">Select your state</option>
                          {INDIAN_STATES.map((state) => (
                            <option key={state} value={state}>
                              {state}
                            </option>
                          ))}
                        </select>
                      </FormGroup>

                      <FormGroup>
                        <label>Pincode</label>
                        <input
                          type="text"
                          name="pincode"
                          value={formData.pincode}
                          onChange={handleFormChange}
                          placeholder="Enter 6-digit pincode"
                          maxLength="6"
                          inputMode="numeric"
                          required
                        />
                        {formData.pincode &&
                          !validatePincode(formData.pincode) && (
                            <span
                              style={{
                                fontSize: "12px",
                                color: "#d32f2f",
                                marginTop: "4px",
                              }}
                            >
                              Pincode must be exactly 6 digits
                            </span>
                          )}
                      </FormGroup>

                      <FormGroup>
                        <label>Country</label>
                        <input
                          type="text"
                          name="country"
                          value={formData.country}
                          onChange={handleFormChange}
                          placeholder="Enter country"
                          required
                        />
                      </FormGroup>

                      <FormGroup fullWidth>
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            fontWeight: "500",
                          }}
                        >
                          <input
                            type="checkbox"
                            name="is_default"
                            checked={formData.is_default}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                is_default: e.target.checked,
                              }))
                            }
                            style={{ width: "auto", cursor: "pointer" }}
                          />
                          Set as default address
                        </label>
                      </FormGroup>

                      <FormGroup fullWidth>
                        <FormActions>
                          <CancelBtn
                            type="button"
                            onClick={handleCancelForm}
                            disabled={savingAddress}
                          >
                            Cancel
                          </CancelBtn>
                          <SaveBtn type="submit" disabled={savingAddress}>
                            {savingAddress ? "Saving..." : "Save Address"}
                          </SaveBtn>
                        </FormActions>
                      </FormGroup>
                    </AddressForm>
                  )}
                </SectionContent>
              </Section>

              {/* Edit Address Modal */}
              {editingAddressId && editFormData && (
                <EditModalOverlay onClick={handleCancelEdit}>
                  <EditModalDialog onClick={(e) => e.stopPropagation()}>
                    <h2>Edit Address</h2>

                    <AddressForm
                      onSubmit={handleEditSubmit}
                      style={{
                        marginBottom: "0",
                        padding: "0",
                        background: "transparent",
                      }}
                    >
                      {editError && (
                        <div
                          style={{
                            gridColumn: "1 / -1",
                            background: "#f8d7da",
                            color: "#721c24",
                            padding: "12px",
                            borderRadius: "4px",
                            fontSize: "13px",
                            marginBottom: "10px",
                          }}
                        >
                          {editError}
                        </div>
                      )}

                      <FormGroup>
                        <label>Name</label>
                        <input
                          type="text"
                          name="name"
                          value={editFormData.name}
                          onChange={handleEditFormChange}
                          placeholder="Enter name"
                          required
                        />
                      </FormGroup>

                      <FormGroup>
                        <label>Phone Number</label>
                        <input
                          type="tel"
                          name="phone"
                          value={editFormData.phone}
                          onChange={handleEditFormChange}
                          placeholder="Enter 10-digit phone number"
                          maxLength="10"
                          inputMode="numeric"
                          required
                        />
                        {editFormData.phone &&
                          !validatePhone(editFormData.phone) && (
                            <span
                              style={{
                                fontSize: "12px",
                                color: "#d32f2f",
                                marginTop: "4px",
                              }}
                            >
                              Phone number must be exactly 10 digits
                            </span>
                          )}
                      </FormGroup>

                      <FormGroup fullWidth>
                        <label>Address</label>
                        <textarea
                          name="address"
                          value={editFormData.address}
                          onChange={handleEditFormChange}
                          placeholder="Enter street address, house no., building name"
                          required
                        />
                      </FormGroup>

                      <FormGroup>
                        <label>City</label>
                        <input
                          type="text"
                          name="city"
                          value={editFormData.city}
                          onChange={handleEditFormChange}
                          placeholder="Enter city"
                          required
                        />
                      </FormGroup>

                      <FormGroup>
                        <label>State</label>
                        <select
                          name="state"
                          value={editFormData.state}
                          onChange={handleEditFormChange}
                          required
                          style={{
                            padding: "10px 12px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            fontSize: "14px",
                            fontFamily: "inherit",
                            transition: "border-color 0.3s",
                            cursor: "pointer",
                            backgroundColor: "#fff",
                          }}
                        >
                          <option value="">Select your state</option>
                          {INDIAN_STATES.map((state) => (
                            <option key={state} value={state}>
                              {state}
                            </option>
                          ))}
                        </select>
                      </FormGroup>

                      <FormGroup>
                        <label>Pincode</label>
                        <input
                          type="text"
                          name="pincode"
                          value={editFormData.pincode}
                          onChange={handleEditFormChange}
                          placeholder="Enter 6-digit pincode"
                          maxLength="6"
                          inputMode="numeric"
                          required
                        />
                        {editFormData.pincode &&
                          !validatePincode(editFormData.pincode) && (
                            <span
                              style={{
                                fontSize: "12px",
                                color: "#d32f2f",
                                marginTop: "4px",
                              }}
                            >
                              Pincode must be exactly 6 digits
                            </span>
                          )}
                      </FormGroup>

                      <FormGroup>
                        <label>Country</label>
                        <input
                          type="text"
                          name="country"
                          value={editFormData.country}
                          onChange={handleEditFormChange}
                          placeholder="Enter country"
                          required
                        />
                      </FormGroup>

                      <FormGroup fullWidth>
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            fontWeight: "500",
                          }}
                        >
                          <input
                            type="checkbox"
                            name="is_default"
                            checked={editFormData.is_default}
                            onChange={(e) =>
                              setEditFormData((prev) => ({
                                ...prev,
                                is_default: e.target.checked,
                              }))
                            }
                            style={{ width: "auto", cursor: "pointer" }}
                          />
                          Set as default address
                        </label>
                      </FormGroup>

                      <FormGroup fullWidth>
                        <FormActions>
                          <CancelBtn
                            type="button"
                            onClick={handleCancelEdit}
                            disabled={savingEdit}
                          >
                            Cancel
                          </CancelBtn>
                          <SaveBtn type="submit" disabled={savingEdit}>
                            {savingEdit ? "Saving..." : "Update Address"}
                          </SaveBtn>
                        </FormActions>
                      </FormGroup>
                    </AddressForm>
                  </EditModalDialog>
                </EditModalOverlay>
              )}

              {/* Payment Section */}
              <Section active={activeSection === "payment"} completed={false}>
                <SectionHeader
                  onClick={() =>
                    addressCompleted && setActiveSection("payment")
                  }
                  disabled={!addressCompleted}
                >
                  <h2>
                    <SectionNumber completed={false}>2</SectionNumber>
                    Payment Method
                  </h2>
                  <span>{activeSection === "payment" ? "−" : "+"}</span>
                </SectionHeader>

                <SectionContent open={activeSection === "payment"}>
                  {selectedAddress && (
                    <PaymentGrid>
                      <PaymentSection>
                        <h4>Delivery Address</h4>
                        <AddressSummary>
                          <p>
                            <strong>{selectedAddress.name}</strong>
                          </p>
                          <p>{selectedAddress.street}</p>
                          <p>
                            {selectedAddress.city}, {selectedAddress.state}{" "}
                            {selectedAddress.pincode}
                          </p>
                          <p>{selectedAddress.phone}</p>
                        </AddressSummary>
                      </PaymentSection>

                      <PaymentSection>
                        <h4>Order Summary</h4>
                        <div>
                          {cartItems.map((item) => (
                            <OrderItem key={item.id}>
                              <img src={item.image} alt={item.name} />
                              <div className="details">
                                <p className="name">{item.name}</p>
                                <p className="qty">Qty: {item.quantity}</p>
                              </div>
                              <div className="price">
                                ₹
                                {(item.price * item.quantity).toLocaleString(
                                  "en-IN",
                                )}
                              </div>
                            </OrderItem>
                          ))}
                        </div>
                      </PaymentSection>
                    </PaymentGrid>
                  )}

                  {/* <div style={{ marginTop: "20px" }}>
                  <h4
                    style={{
                      fontSize: "14px",
                      fontWeight: "700",
                      marginBottom: "12px",
                      color: "#000",
                      textTransform: "uppercase",
                    }}
                  >
                    Select Payment Method
                  </h4>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "12px",
                      marginBottom: "20px",
                    }}
                  >
                    {["Credit Card", "Debit Card", "UPI", "Net Banking"].map(
                      (method) => (
                        <button
                          key={method}
                          style={{
                            padding: "12px",
                            border: "1px solid #ddd",
                            borderRadius: "6px",
                            background: "#fff",
                            cursor: "pointer",
                            fontSize: "13px",
                            fontWeight: "600",
                            transition: "all 0.3s",
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.borderColor = "#000";
                            e.target.style.background = "#f9f9f9";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.borderColor = "#ddd";
                            e.target.style.background = "#fff";
                          }}
                        >
                          {method}
                        </button>
                      ),
                    )}
                  </div>
                </div> */}

                  {checkoutError && (
                    <div
                      style={{
                        background: "#f8d7da",
                        color: "#721c24",
                        padding: "12px 16px",
                        borderRadius: "4px",
                        fontSize: "13px",
                        marginBottom: "20px",
                        border: "1px solid #f5c6cb",
                      }}
                    >
                      {checkoutError}
                    </div>
                  )}

                  <ProceedPaymentBtn
                    disabled={
                      !selectedAddress ||
                      processingCheckout ||
                      loadingPaymentConfig
                    }
                    onClick={handleCheckout}
                  >
                    {processingCheckout
                      ? "Processing..."
                      : loadingPaymentConfig
                        ? "Loading Payment Gateway..."
                        : "Proceed to Payment"}
                  </ProceedPaymentBtn>
                </SectionContent>
              </Section>
            </SectionContainer>

            {/* Order Summary Sidebar */}
            <OrderSummary>
              <h3
                style={{
                  fontSize: "16px",
                  fontWeight: "700",
                  margin: "0 0 20px 0",
                }}
              >
                Order Summary
              </h3>

              {loadingCart ? (
                <OrderSummarySkeleton itemCount={cartItems.length || 3} />
              ) : (
                <>
                  <div style={{ marginBottom: "20px" }}>
                    {cartItems.map((item) => (
                      <SummaryItem key={item.id}>
                        <div className="label">
                          {item.name} x {item.quantity}
                        </div>
                        <div className="value">
                          ₹
                          {(item.price * item.quantity).toLocaleString("en-IN")}
                        </div>
                      </SummaryItem>
                    ))}
                  </div>

                  <SummaryItem>
                    <div className="label">Subtotal</div>
                    <div className="value">
                      ₹{subtotal.toLocaleString("en-IN")}
                    </div>
                  </SummaryItem>

                  <SummaryItem>
                    <div className="label">Shipping</div>
                    <div className="value">
                      {SHIPPING_COST === 0 ? (
                        <span style={{ color: "#28a745" }}>FREE</span>
                      ) : (
                        `₹${SHIPPING_COST.toLocaleString("en-IN")}`
                      )}
                    </div>
                  </SummaryItem>

                  <ShippingNote>
                    {SHIPPING_COST === 0 ? (
                      <span>✓ FREE Shipping on orders above ₹499</span>
                    ) : (
                      <span>
                        Add ₹
                        {(500 - Math.ceil(subtotal)).toLocaleString("en-IN")}{" "}
                        more for free shipping
                      </span>
                    )}
                  </ShippingNote>

                  {/* Coupon Section */}
                  <div style={{ marginBottom: "20px" }}>
                    {appliedCoupon ? (
                      <CouponAppliedBox>
                        <CouponAppliedHeader>
                          <CouponAppliedTitle>
                            ✓ Coupon Applied Successfully
                          </CouponAppliedTitle>
                          <CouponRemoveBtn onClick={handleRemoveCoupon}>
                            Remove
                          </CouponRemoveBtn>
                        </CouponAppliedHeader>
                        <CouponInfo>
                          <div>
                            <strong>Code:</strong>{" "}
                            <span>{appliedCoupon.code}</span>
                          </div>
                          {appliedCoupon.discount_type && (
                            <div style={{ marginTop: "6px" }}>
                              <strong>Discount Type:</strong>{" "}
                              {appliedCoupon.discount_type === "flat"
                                ? `₹${appliedCoupon.discount_value} Off`
                                : `${appliedCoupon.discount_value}% Off`}
                            </div>
                          )}
                          {appliedCoupon.user_usage_limit && (
                            <div
                              style={{
                                marginTop: "6px",
                                fontSize: "11px",
                                color: "#999",
                              }}
                            >
                              Usage Limit: {appliedCoupon.user_usage_limit}
                            </div>
                          )}
                        </CouponInfo>
                      </CouponAppliedBox>
                    ) : (
                      <CouponSection>
                        <CouponInput
                          type="text"
                          placeholder="Enter coupon code"
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value)}
                          disabled={couponLoading}
                          onKeyPress={(e) => {
                            if (e.key === "Enter") {
                              handleApplyCoupon();
                            }
                          }}
                        />
                        <CouponButton
                          onClick={handleApplyCoupon}
                          disabled={couponLoading || !couponCode.trim()}
                        >
                          {couponLoading ? "Applying..." : "Apply"}
                        </CouponButton>
                      </CouponSection>
                    )}
                    {couponError && <CouponError>{couponError}</CouponError>}
                  </div>

                  {discount > 0 && (
                    <DiscountRow>
                      <div className="label">Discount</div>
                      <div className="value">
                        -₹{discount.toLocaleString("en-IN")}
                      </div>
                    </DiscountRow>
                  )}

                  <SummaryItem className="total">
                    <div className="label">Total</div>
                    <div className="value">
                      ₹{total.toLocaleString("en-IN")}
                    </div>
                  </SummaryItem>
                </>
              )}
            </OrderSummary>
          </CheckoutContent>
        )}
      </CheckoutWrapper>

      <Footer />
    </Container>
  );
};

export default Checkout;
